(ns app.front.view.property.view
  (:require [app.domain.group.config.model :as config-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.table :as ui-table]
            [app.front.util :refer [for-frag]]
            [app.front.view.complaint.complaints-section :as complaints-section]
            [app.front.view.fee.summary :as fee-summary]
            [app.front.view.inspections.summary-subsection :as inspections-summary-subsection]
            [app.front.view.licence.table :as licence-table]
            [app.front.view.project.conditions :as conditions]
            [app.front.view.property.header :as header]
            [app.front.view.property.subs :as property-subs]
            [app.front.view.property.v2.view :as property-view-v2]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [core.db.entity-api :as entity-api]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [schema.core :as s]))


(defn section [{:keys [tr title heading-extra initially-collapsed?]} content]
  [section/section
   title
   (util/mmap tr heading-extra initially-collapsed?)
   title
   content])

(defn make-inspections-subsection-props [{workspace-id  :workspace/id
                                          permit-number :workspace/municipal-case-id
                                          :as           workspace}]
  (let [lang (<sub [:language])]
    {:title    (cc/link {:href       (ws-util/workspace-href workspace)
                         :class-name "font-bold"}
                        (or permit-number workspace-id))
     :subtitle (fee-summary/target-and-category lang workspace)}))

(defn workspace-render [permit-type tr]
  (let [date-title        (cond
                            (= permit-type :permit-type/B) :property/ws-b-date
                            (= permit-type :permit-type/P) :property/ws-p-date
                            (= permit-type :permit-type/PW) :property/ws-pw-date
                            (= permit-type :permit-type/CE) :property/ws-ce-date
                            (= permit-type :permit-type/LU) :property/ws-lu-date)
        items             (<sub [::property-subs/workspace-items permit-type])
        format-date       (<sub [::date-util/date-fn])
        format-date-in-tz (fn [{:keys [tz date] :application/keys [approved]}]
                            (or (some-> (if (= permit-type :permit-type/P)
                                          approved
                                          date)
                                        (date-util/in-tz tz)
                                        format-date)
                                "-"))]
    [:<>
     [ui-table/modern-responsive-table
      {:items   items
       :cols    (concat [[:property/ws-status :state {:sorting true}]
                         [date-title format-date-in-tz {:sorting true}]
                         [:property/ws-category :category {:sorting true}]]
                        (when (not= permit-type :permit-type/CE) [[:property/ws-work-type :work-type {:sorting true}]
                                                                  [:property/ws-work-target :work-target {:sorting true}]])
                        [[:property/ws-property-owner :owners {:sorting true}]])
       :tr      tr
       :id-func :workspace-id
       :link    {:make-href   :ws-link
                 :description ws-util/workspace-link-description}}]]))


(defn complaint-render [tr]
  (let [items (<sub [::property-subs/complaint-items])]
    [complaints-section/complaint-table tr items false]))

(defn licence-table [{:keys [tr lang licences]}]
  [ui-table/modern-responsive-table
   {:class      "w-full mt-2"
    :cols       [[:licence/issued-to
                  (fn [{:licence/keys [holder-names]}]
                    (i18n-model/get-text-from-entities-or-map holder-names lang))]

                 (licence-table/state-column tr)

                 [:licence/type
                  (fn [{:licence/keys [category-group-config]}]
                    (config-model/localized-name category-group-config lang))]

                 licence-table/issue-date-column
                 licence-table/expiry-date-column]
    :link       {:make-href   licence-table/href
                 :description :licence/go-to}
    :get-status licence-table/get-status
    :items      licences
    :tr         tr}])

(defn licences-section [{:keys [tr lang]}]
  (let [show-invalid?    (boolean (<sub [::property-subs/show-invalid-licences?]))
        valid-licences   (<sub [::property-subs/valid-licences])
        invalid-licences (<sub [::property-subs/invalid-licences])]
    [:<>
     [:div
      [:h3.text-lg.mb-4 (tr [:licence/valids])]
      [licence-table (util/mmap {:licences valid-licences}
                                tr
                                lang)]]
     (when (seq invalid-licences)
       [:div.mt-4
        [ui/expand-button
         {:title-expanded  (tr [:licence/expireds-hide])
          :title-collapsed (tr [:licence/expireds-show])
          :expanded?       show-invalid?
          :tr              tr
          :expand-handler  #(>evt [::property-subs/toggle-show-invalid-licences?])}]
        (when show-invalid?
          [:div.mt-6
           [licence-table (util/mmap {:licences invalid-licences}
                                     tr
                                     lang)]])])]))

(defn month-select [tr]
  (let [selected-months (<sub [::property-subs/complaints-months])]
    [:div.pb-6
     [ui-select/select
      {:id           "month-select"
       :allow-empty? false
       :values       [3 6 9 12 :all]
       :value        (or selected-months 12)
       :label-text   (tr [:complaint/month-select])
       :inline       true
       :val->str     (fn [month]
                       (if (= month :all)
                         (tr [:complaint/all-months])
                         (str month)))
       :on-change    #(>evt [::property-subs/change-complaint-month-selection %])}]]))

(def permit-type->title {:permit-type/B  :property/building-title
                         :permit-type/P  :property/planning-title
                         :permit-type/PW :property/public-works-title
                         :permit-type/CE :property/code-enforcement-title
                         :permit-type/LU :property/land-use-title})

(defn property-view []
  (let [tr                 (<sub [::property-subs/tr])
        lang               (<sub [:language])
        show-orders?       (<sub [::property-subs/has-orders?])
        show-deficiencies? (<sub [::property-subs/has-deficiencies?])
        property           (<sub [::property-subs/property])]
    (if (<sub [::property-subs/loading?])
      (cc/loading-spinner)
      [:div.my-5.mx-auto.cloudpermit-page
       [header/header tr property]
       [:section
        [:p.pb-0 (tr [:property/note])]]
       (for-frag [permit-type [:permit-type/B :permit-type/P :permit-type/PW :permit-type/CE :permit-type/LU]]
         (when-not (empty? (<sub [::property-subs/workspaces-by-permit-type permit-type]))
           (let [tr (<sub [::property-subs/tr-for-permit-type permit-type])]
             [section (util/mmap {:title (tr [(permit-type permit-type->title)])} tr)
              [workspace-render permit-type tr]])))
       (when (<sub [::property-subs/has-complaints?])
         [section (util/mmap {:title (tr [:property/complaint-title])} tr)
          [:div
           [month-select tr]
           [complaint-render tr]]])
       (when (<sub [::property-subs/has-conditions?])
         [section (util/mmap {:title (tr [:property/conditions-title])} tr)
          [conditions/conditions-list (<sub [::property-subs/conditions])]])
       (when (seq (<sub [::property-subs/licences]))
         [section (util/mmap {:title (tr [:property/licences-title])} tr)
          [licences-section (util/mmap tr lang)]])
       (when (<sub [::property-subs/has-inspections?])
         [section (util/mmap {:title (tr [:property/inspections-title])} tr)
          [inspections-summary-subsection/inspections
           (util/mmap {:workspaces-sub        [::property-subs/workspaces]
                       :reverse?              true
                       :make-subsection-props make-inspections-subsection-props
                       :hide-empty?           true}
                      tr)]])
       (when (or show-deficiencies? show-orders?)
         [section (util/mmap {:title (tr [:property/deficiencies-and-orders])} tr)
          [inspections-summary-subsection/deficiencies-and-orders
           (util/mmap {:show-orders?     show-orders?
                       :orders-sub       [::property-subs/orders]
                       :deficiencies-sub [::property-subs/deficiencies]}
                      tr)]])
       (when (<sub [::property-subs/has-bills?])
         [section (util/mmap {:title (tr [:property/bills])} tr)
          [fee-summary/bills-list (util/mmap {:bills-sub [::property-subs/bills]}
                                             tr)]])])))

(defn property-view-adapter []
  (if (<sub [::ff/enabled? :feature-flag.id/property-management])
    [property-view-v2/property-view]
    [property-view]))

(defn workspace-by-ref [{:keys [match]}]
  (let [{:keys [workspace-ref]} (-> match :query-params)
        workspace               (when workspace-ref
                                  (<sub [::entities/entity {:workspace/id workspace-ref}]))]
    [:view/workspace
     {:workspace-id workspace-ref}
     (or (:workspace/municipal-case-id workspace) (:workspace/id workspace))]))

(defn project-by-ref [{:keys [tr match]}]
  (let [{:keys [workspace-ref]} (-> match :query-params)
        workspace               (when workspace-ref
                                  (<sub [::entities/entity {:workspace/id workspace-ref}]))
        project                 (when (entity-api/entity? (:entity/parent workspace))
                                  (:entity/parent workspace))
        project-id              (or (:db/id (:entity/parent workspace))
                                    (:entity/parent workspace))]
    [:view/project {:project-id project-id} (or (:project/name project) (tr [:applications/project]))]))

(defn property-by-name [{:keys [tr match]}]
  (let [{:keys [id-domain verified-id db-id]
         :as   params}                       (-> match :parameters :path)
        property                             (if verified-id
                                               (<sub [::entities/entity {:property/id+id-domain [verified-id (keyword id-domain)]}])
                                               (when db-id (<sub [::entities/entity db-id])))
        route-name                           (-> match :data :name)]
    [route-name params (or (some-> property
                                   :property/primary-address
                                   :address/full-address)
                           (tr [:address/address-missing]))]))

(def breadcrumb
  {:items [project-by-ref
           workspace-by-ref
           property-by-name]})

(def route
  {:name        :view/property
   :view        property-view-adapter
   :view-sub    [::property-subs/view-data]
   :breadcrumb  breadcrumb
   :parameters  {:path {:db-id s/Int}}
   :controllers [{:parameters {:path [:db-id]}
                  :start      (fn [params]
                                (>evt [::property-subs/get-property-data (-> params :path :db-id)]))}]})

(def verified-id-route
  {:name        :view/property-by-id
   :view        property-view-adapter
   :view-sub    [::property-subs/view-data]
   :breadcrumb  breadcrumb
   :parameters  {:path {:id-domain   s/Str
                        :verified-id s/Str}}
   :controllers [{:parameters {:path [:id-domain :verified-id]}
                  :start      (fn [{{:keys [id-domain verified-id]} :path}]
                                (>evt [::property-subs/get-property-data-by-subdiv id-domain verified-id]))}]})
